/** @jsx jsx */
import { jsx, Container, Flex, Box } from 'theme-ui';
/* eslint-disable react/no-danger */
import React, { useMemo } from 'react';
import BackgroundImage from 'gatsby-background-image';

// Node Types
import { SectionComponent } from '@codersclan/gatsby-theme-airfleet/src/@types';

// Node Components
import FluidImage from '@codersclan/gatsby-theme-airfleet/src/components/fluidImage';

// Types
import { Section } from '../../@codersclan/gatsby-theme-airfleet/@types/section';

// Components
import Text, { TEXT_TYPES } from '../../components/text';
import StarRating from '../../components/starRating';

// Styles
import * as style from './style';

interface SectionComponentProps extends Section {
  reviewBackground: {
    alternativeText?: string;
    image: ImageType;
  };
  reviewer: {
    companyOrPerson: string;
    company: {
      image: ImageType;
    };
    person: {
      userCompanyText: string;
      userNameText: string;
      userPositionText: string;
      image: ImageType;
    };
  };
  reviewText: {
    reviewText: string;
  };
  reviewTitle: {
    reviewTitle: string;
  };
  reviewStars?: {
    alternativeText?: string;
    reviewStars: string;
    image?: ImageType;
  };
}

const CompanyUserReviewQuote: SectionComponent<SectionComponentProps> = React.memo(
  ({ reviewBackground, reviewTitle, reviewText, reviewer, reviewStars }) => {
    const quoteImage = useMemo(() => {
      if (reviewer.companyOrPerson === 'company') {
        return (
          <FluidImage {...reviewer.company.image} sx={style.CompanyImage} />
        );
      }
      return <FluidImage {...reviewer.person.image} sx={style.PersonImage} />;
    }, [reviewer.companyOrPerson, reviewer.company, reviewer.person]);

    // Content
    const content = (
      <Container sx={style.BaseContainer}>
        <Flex sx={style.ContentContainer}>
          <Flex sx={style.QuoteImageContainer}>{quoteImage}</Flex>
          <Flex sx={style.QuoteTextContainer}>
            <Text type={TEXT_TYPES.H1_BOLD} sx={style.ReviewTitle}>
              {reviewTitle.reviewTitle}
            </Text>
            <Flex sx={style.PersonRatingCompanyLogoContainer}>
              {reviewStars && (
                <StarRating amount={parseInt(reviewStars.reviewStars, 10)} />
              )}
              {reviewer.companyOrPerson === 'company' && reviewStars?.image && (
                <FluidImage {...reviewStars.image} />
              )}
            </Flex>
            <Text type={TEXT_TYPES.HERO_SUBHEAD} sx={style.ReviewText}>
              {reviewText.reviewText}
            </Text>
            {reviewer.companyOrPerson === 'person' && (
              <Box sx={style.PersonInfoContainer}>
                <Text type={TEXT_TYPES.H4} sx={style.PersonInfoName}>
                  {reviewer.person.userNameText}
                </Text>
                <Text type={TEXT_TYPES.H4}>
                  {reviewer.person.userCompanyText}
                </Text>
                <Text type={TEXT_TYPES.H4}>
                  {reviewer.person.userPositionText}
                </Text>
              </Box>
            )}
          </Flex>
        </Flex>
      </Container>
    );

    if (reviewBackground.image.localFile?.childImageSharp?.fluid) {
      return (
        <BackgroundImage
          fluid={reviewBackground.image.localFile?.childImageSharp?.fluid}
        >
          {content}
        </BackgroundImage>
      );
    }
    return content;
  },
);

export default CompanyUserReviewQuote;
